import { DataStore, ItemReference, DataModel, Query, QueryResult } from 'src/core/stores/dataStore';
import { FormStore, FormModel } from 'src/core/stores/formStore';
import { repository, AsyncAction, reduce } from 'redux-scaffolding-ts';
import { container } from 'src/inversify.config';
import { Validator } from "lakmus";
import { CommandResult } from '../core/stores/types';
import { AxiosResponse } from 'axios';
import HttpService from '../core/services/http.service';

export interface EmployeeSummary {
    id: number;
    restrictionPlanId: string | null,
    restrictionPlanTitle: string | null,
    title: string;
    function: string;
    identityName: string;
    managerId: string;
    managerTitle: string;
    cloneTimesheet: boolean;
    isUnlinked: boolean;


    mondayMinHours: number;
    mondayMaxHours: number;
    tuesdayMinHours: number;
    tuesdayMaxHours: number;
    wednesdayMinHours: number;
    wednesdayMaxHours: number;
    thursdayMinHours: number;
    thursdayMaxHours: number;
    fridayMinHours: number;
    fridayMaxHours: number;
    saturdayMinHours: number;
    saturdayMaxHours: number;
    sunMinHours: number;
    sunMaxHours: number;
    weekMinHours: number;
    weekMaxHours: number;
    ciiEligible: boolean;
    cirEligible: boolean;
}

export interface IdentityUserSummary {
    firstName: string
    lastName: string
    email: string
    id: string
}

export interface EmployeeItemReference {
    id: number
    title: string
    restrictionPlanId: string | null
}

export interface NewEmployeeItem {
    name: string;
    lastName: string;
    title: string;
    function: string;
    restrictionPlanId: string | undefined,
    identityName: string;
    isUnlinked: boolean;
    managerId: string;
    notifyByEmail: string;
    cloneTimesheet: boolean;
    timesheetReminder: number;
    teamReport: number;
    cirEligible: boolean;
    ciiEligible: boolean;
    estimatedHourlyCost: number;
    timesheetStatusNotificationEmployee: boolean;
    timesheetStatusNotificationManager: boolean;
    language: string;
    currency: string;
    deletedProjectNotification: boolean
    code: string;
}

export interface EmployeeItem extends NewEmployeeItem {
    name: string,
    email: string,
    id: number;
    allowedCir: boolean;
    allowedCii: boolean;
}

export interface EmployeeCompanyDto {
    key: string;
    id: any;
    name: string;
    email: string;
    companyId: any;
    companyName: string;
}

@repository("@@EMPLOYEES", "employees.summary")
export class EmployeesStore extends DataStore<EmployeeSummary> {
    baseUrl: string = ``;

    constructor() {
        super('EMPLOYEE', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<EmployeeSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<EmployeeSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }

    public async getAllDeletedAsync(query: Query, data?: any): Promise<QueryResult<EmployeeSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<EmployeeSummary>>(`${this.baseUrl}/deleted?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }

    public async restoreEmployee(id: string) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.post(`${this.baseUrl}/${id}/restore`, {});
        return result.data;
    }

    public async hardDeleteEmployee(id: string) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.delete(`${this.baseUrl}/delete/${id}`, {});
        return result.data;
    }

    public async inviteEmployee(id: string) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.post(`${this.baseUrl}/${id}/send-invitation`, {});
        return result.data;
    }
    public async employeesHierarchy(deleted: boolean = false) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.get(`${this.baseUrl}/hierarchy/?deleted=${deleted}`, {});
        return result.data;
    }

    public async checkEmployeeExists(): Promise<boolean | null> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result: AxiosResponse<boolean | null>  = await httpService.get(`${this.baseUrl}/checkEmployeeExists`, {});
        return result.data;
    }

    public async exportEmployees(query: Query, employeesIds: string[]) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.post(`${this.baseUrl}/exportEmployees?${DataStore.buildUrl(query)}`, employeesIds);
        return result.data;
    }
}

@repository("@@EMPLOYEES", "employees.report")
export class ReportEmployeesStore extends DataStore<EmployeeSummary> {
    baseUrl: string = ``;

    constructor() {
        super('EMPLOYEE', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }

    public changeAdminUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees`;
    }
    public changeUserUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<EmployeeSummary>> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<EmployeeSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data));
        return result.data;
    }
}

@repository("@@EMPLOYEES", "employees.dropdown")
export class DropDownEmployeesStore extends DataStore<EmployeeItemReference> {
    baseUrl: string = ``;

    constructor() {
        super('EMPLOYEEDROPDOWN', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container)
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<EmployeeSummary>> {
        let httpService = this.container.get<HttpService>(HttpService)
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<EmployeeSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data))
        return result.data
    }
}

@repository("@@EMPLOYEES", "employees.managersDropdown")
export class DropDownManagersStore extends DataStore<EmployeeItemReference> {
    baseUrl: string = ``;

    constructor() {
        super('EMLOYEESMANAGER', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container)
    }

    public changeUrl(companyId: string, employeeId: number) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees/managers/${employeeId}`;
    }

    public async getAllAsync(query: Query, data?: any): Promise<QueryResult<EmployeeSummary>> {
        let httpService = this.container.get<HttpService>(HttpService)
        const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<EmployeeSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data))
        return result.data
    }
}

export class NewEmployeeValidator extends Validator<NewEmployeeItem> {
    constructor() {
        super();

        this.ruleFor(x => x.identityName)
            .notNull()
            .email()
            .withMessage("A valid email is required");
    }
}

export class EmployeeValidator extends Validator<EmployeeItem> {
    constructor() {
        super();

        this.ruleFor(x => x.identityName)
            .notNull()
            .email()
            .withMessage("A valid email is required");
    }
}

const EMPLOYEE_UPDATE_ITEM = "EMPLOYEE_UPDATE_ITEM";
@repository("@@EMPLOYEES", "employees.detail")
export class EmployeeStore extends FormStore<EmployeeItem> {
    baseUrl: string = ``;

    protected validate(item: EmployeeItem) {
        return new EmployeeValidator().validate(item);
    }

    constructor() {
        super('EMPLOYEE', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
        this.addReducer(EMPLOYEE_UPDATE_ITEM, (): any => {
            return super.onPatch()
        }, 'AsyncAction');
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees`;
    }

    public async getById(id: string, params?: any, companyId?: string): Promise<EmployeeItem> {
        var httpService = this.container.get<HttpService>(HttpService);
        const result = await this.dispatchAsync(this.ENTITY_LOAD, httpService.get<EmployeeItem>(`${this.baseUrl}/${id}`, params));
        return result.data;
    }

    public async getIdentityUser(id: string, data?: any): Promise<IdentityUserSummary> {
        return new Promise<IdentityUserSummary>((resolve, reject) => {
            let httpService = this.container.get<HttpService>(HttpService);
            httpService.get<IdentityUserSummary>(`${this.baseUrl}/${id}/identity`, data).then((result) => {
                if (result.data) {
                    resolve(result.data)
                } else {
                    resolve(undefined)
                }
            }).catch((reason) => {
                reject(reason)
            })
        })
    }

    public async update(item: EmployeeItem) {
        var result = await super.patch(EMPLOYEE_UPDATE_ITEM, `${item.id}`, item) as any;
        return result.data as CommandResult<EmployeeItem>;
    }
    public async updateNotifications(item: EmployeeItem) {
        var result = await super.patch(EMPLOYEE_UPDATE_ITEM, `settings/${item.id}`, item) as any;
        return result.data as CommandResult<EmployeeItem>;
    }
    public async getRoles(id: string, use_cache: boolean = true) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.get(`${this.baseUrl}/${id}/roles/?use_cache=${use_cache}`, {});
        return result.data;
    }
    public async employeeHierarchy(id: string, deleted: boolean = false) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.get(`${this.baseUrl}/hierarchy/${id}/?deleted=${deleted}`, {});
        return result.data;
    }
}

@repository("@@EMPLOYEES", "employees.new")
export class NewEmployeeStore extends FormStore<NewEmployeeItem> {
    baseUrl: string = ``;

    protected validate(item: NewEmployeeItem) {
        return (new NewEmployeeValidator()).validate(item);
    }

    constructor() {
        super('NEW_EMPLOYEE', {
            isBusy: false,
            status: 'New',
            item: undefined,
            result: undefined
        }, container);
    }

    public changeUrl(companyId: string) {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/employees`;
    }

    public async getRoles(id: string) {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.get(`${this.baseUrl}/${id}/roles`, {});
        return result.data;
    }
}

@repository("@@EMPLOYEES", "employees.summary")
export class EmployeeCompanyStore extends DataStore<EmployeeCompanyDto> {
    baseUrl: string = ``;

    constructor() {
        super('EMPLOYEE', {
            count: 0,
            isBusy: false,
            items: [],
            result: undefined,
            discard: item => { }
        }, container);
    }
    public changeEployeeUrl() {
        this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/'00000000-0000-0000-0000-000000000000'/employees`;
    }

    public async getEmployeesCompanies(searchValue: string): Promise<EmployeeCompanyDto[]> {
        let httpService = this.container.get<HttpService>(HttpService);
        const result = await httpService.get<EmployeeCompanyDto[]>(`${this.baseUrl}/EmployeesCompanies/${searchValue}`, {});
        return result.data;
    }
}
